<template>
  <div class="cover-inpainting">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center" style="gap: 0.5rem;">
        <b-button
            :variant="isDrawing ? 'danger' : 'outline-primary'"
            size="sm"
            @click="toggleDrawing"
        >
          <feather-icon :icon="isDrawing ? 'XIcon' : 'Edit2Icon'" class="mr-25" />
          {{ isDrawing ? 'Отключить кисть' : 'Редактировать' }}
        </b-button>
        <div v-if="isDrawing" class="d-flex align-items-center" style="gap: 0.5rem;">
          <label class="mb-0 font-small-3">Размер кисти:</label>
          <input
              type="range"
              :min="5"
              :max="80"
              v-model.number="brushSize"
              style="width: 120px;"
          />
          <span class="font-small-3 text-muted">{{ brushSize }}px</span>
          <b-button
              variant="outline-warning"
              size="sm"
              @click="clearMask"
              :disabled="!hasMaskStrokes"
          >
            <feather-icon icon="Trash2Icon" class="mr-25" />
            Очистить
          </b-button>
        </div>
      </div>
      <div v-if="isProcessing" class="d-flex align-items-center text-info">
        <b-spinner small class="mr-50" />
        <span class="font-small-3">Обработка...</span>
      </div>
    </div>

    <div class="d-flex justify-content-center">
      <div
          ref="canvasContainer"
          class="position-relative d-inline-block"
          style="line-height: 0;"
      >
        <img
            ref="baseImage"
            :src="displayImageUrl"
            class="rounded"
            :class="{ 'cover-processing': isProcessing }"
            style="max-height: 600px; max-width: 100%;"
            @load="onImageLoad"
        />
      <canvas
          v-show="isDrawing"
          ref="maskCanvas"
          class="position-absolute rounded"
          style="top: 0; left: 0; cursor: crosshair; opacity: 0.65;"
          @mousedown="startStroke"
          @mousemove="continueStroke"
          @mouseup="endStroke"
          @mouseleave="endStroke"
      />
      </div>
    </div>

    <div class="mt-1">
      <span v-if="album.release_date" class="text-muted">{{ album.release_date }}</span>
    </div>

    <div class="mt-1 d-flex align-items-center" style="gap: 0.5rem;">
      <label class="mb-0 font-small-3">Сложность:</label>
      <b-form-rating
          v-model="coverDifficulty"
          :stars="5"
          inline
          no-border
          size="sm"
          variant="warning"
          class="p-0"
      />
    </div>

    <div v-if="error" class="text-danger mt-50 font-small-3">{{ error }}</div>

    <div class="mt-1 d-flex" style="gap: 0.5rem;">
      <b-button
          v-if="hasResult"
          variant="outline-secondary"
          @click="resetToOriginal"
      >
        <feather-icon icon="RotateCcwIcon" class="mr-50" />
        Вернуть оригинал
      </b-button>
      <b-button
          variant="success"
          :disabled="isSaving || isProcessing"
          @click="handleSave"
      >
        <b-spinner v-if="isSaving" small class="mr-50" />
        <feather-icon v-else icon="DownloadIcon" class="mr-50" />
        <span class="align-middle">{{ isSaving ? 'Сохранение...' : 'Сохранить' }}</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import { ref, onUnmounted, computed, nextTick, watch } from '@vue/composition-api'
import { BButton, BSpinner, BFormRating } from 'bootstrap-vue'
import store from '@/store'
import axios from 'axios'

export default {
  components: {
    BButton,
    BSpinner,
    BFormRating,
  },
  props: {
    album: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const baseImage = ref(null)
    const maskCanvas = ref(null)
    const canvasContainer = ref(null)

    const isDrawing = ref(false)
    const isStroking = ref(false)
    const brushSize = ref(30)
    const hasMaskStrokes = ref(false)
    const isProcessing = ref(false)
    const isSaving = ref(false)
    const error = ref(null)
    const resultImageUrl = ref(null)
    const coverDifficulty = ref(0)

    let debounceTimer = null
    let cancelSource = null
    let maskCtx = null

    const originalImageUrl = computed(() => props.album.cover)
    const currentImageUrl = computed(() => resultImageUrl.value || originalImageUrl.value)
    const displayImageUrl = computed(() => currentImageUrl.value)
    const hasResult = computed(() => !!resultImageUrl.value)

    const DEBOUNCE_MS = 1000

    function onImageLoad() {
      if (isDrawing.value) {
        setupCanvas()
      }
    }

    function setupCanvas() {
      const img = baseImage.value
      const canvas = maskCanvas.value
      if (!img || !canvas) return

      canvas.width = img.clientWidth
      canvas.height = img.clientHeight
      canvas.style.width = img.clientWidth + 'px'
      canvas.style.height = img.clientHeight + 'px'

      maskCtx = canvas.getContext('2d')
      maskCtx.lineCap = 'round'
      maskCtx.lineJoin = 'round'
    }

    function toggleDrawing() {
      isDrawing.value = !isDrawing.value
      if (isDrawing.value) {
        nextTick(() => setupCanvas())
      } else {
        clearDebounce()
        hasMaskStrokes.value = false
      }
    }

    function clearMask() {
      if (!maskCtx || !maskCanvas.value) return
      maskCtx.clearRect(0, 0, maskCanvas.value.width, maskCanvas.value.height)
      hasMaskStrokes.value = false
      clearDebounce()
      cancelPendingRequest()
    }

    function getPos(e) {
      const rect = maskCanvas.value.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    }

    function startStroke(e) {
      isStroking.value = true
      clearDebounce()
      cancelPendingRequest()

      const pos = getPos(e)
      maskCtx.strokeStyle = '#39ff14'
      maskCtx.lineWidth = brushSize.value
      maskCtx.beginPath()
      maskCtx.moveTo(pos.x, pos.y)
      // Рисуем точку при клике
      maskCtx.lineTo(pos.x + 0.1, pos.y + 0.1)
      maskCtx.stroke()
      hasMaskStrokes.value = true
    }

    function continueStroke(e) {
      if (!isStroking.value) return
      const pos = getPos(e)
      maskCtx.lineTo(pos.x, pos.y)
      maskCtx.stroke()
    }

    function endStroke() {
      if (!isStroking.value) return
      isStroking.value = false
      maskCtx.closePath()
      startDebounce()
    }

    function clearDebounce() {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }
    }

    function cancelPendingRequest() {
      if (cancelSource) {
        cancelSource.cancel('Рисование продолжено')
        cancelSource = null
      }
    }

    function startDebounce() {
      clearDebounce()
      if (!hasMaskStrokes.value) return
      debounceTimer = setTimeout(() => {
        sendInpaintingRequest()
      }, DEBOUNCE_MS)
    }

    function exportMaskAsBlob() {
      return new Promise((resolve) => {
        const img = baseImage.value
        const canvas = maskCanvas.value
        if (!img || !canvas) {
          resolve(null)
          return
        }

        // Создаём канвас размером с оригинальное изображение
        const exportCanvas = document.createElement('canvas')
        exportCanvas.width = img.naturalWidth
        exportCanvas.height = img.naturalHeight
        const ctx = exportCanvas.getContext('2d')

        const scaleX = img.naturalWidth / canvas.width
        const scaleY = img.naturalHeight / canvas.height

        // Конвертируем салатовые штрихи в белые на отдельном канвасе
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = exportCanvas.width
        tempCanvas.height = exportCanvas.height
        const tempCtx = tempCanvas.getContext('2d')
        tempCtx.scale(scaleX, scaleY)
        tempCtx.drawImage(canvas, 0, 0)
        // Заменяем цвет на белый только там, где есть штрихи
        tempCtx.setTransform(1, 0, 0, 1, 0, 0)
        tempCtx.globalCompositeOperation = 'source-in'
        tempCtx.fillStyle = '#ffffff'
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)

        // Заполняем чёрным (сохраняемая область) и накладываем белые штрихи
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height)
        ctx.drawImage(tempCanvas, 0, 0)

        exportCanvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/png')
      })
    }

    async function sendInpaintingRequest() {
      if (isProcessing.value) {
        cancelPendingRequest()
      }

      error.value = null
      isProcessing.value = true

      try {
        // 1. Экспортируем маску
        const maskBlob = await exportMaskAsBlob()
        if (!maskBlob) {
          error.value = 'Не удалось создать маску'
          isProcessing.value = false
          return
        }

        // 2. Загружаем маску на сервер
        const maskFile = new File([maskBlob], 'mask.png', { type: 'image/png' })
        const uploadResponse = await store.dispatch('artists/uploadFile', { file: maskFile, type: 'inpainting-mask' })
        const maskUrl = uploadResponse.data.url

        // 3. Отправляем запрос на удаление объекта
        cancelSource = axios.CancelToken.source()
        const response = await store.dispatch('artists/inpaintingRemoveObject', {
          imageUrl: currentImageUrl.value,
          maskUrl,
          cancelToken: cancelSource.token,
        })

        if (response.data.success && response.data.result_url) {
          resultImageUrl.value = response.data.result_url
          // Очищаем маску после успешной обработки
          if (maskCtx && maskCanvas.value) {
            maskCtx.clearRect(0, 0, maskCanvas.value.width, maskCanvas.value.height)
            hasMaskStrokes.value = false
          }
        } else {
          const msg = response.data.error || 'Не удалось обработать изображение'
          error.value = response.data.detail ? `${msg}: ${response.data.detail}` : msg
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Запрос отменён пользователем — не показываем ошибку
          return
        }
        const data = err.response?.data
        const msg = data?.error || data?.message || 'Ошибка при обработке'
        error.value = data?.detail ? `${msg}: ${data.detail}` : msg
      } finally {
        cancelSource = null
        isProcessing.value = false
      }
    }

    function resetToOriginal() {
      resultImageUrl.value = null
      clearMask()
    }

    function handleSave() {
      isSaving.value = true
      emit('save', currentImageUrl.value, () => {
        isSaving.value = false
      }, coverDifficulty.value)
    }

    watch(() => props.album, () => {
      resultImageUrl.value = null
      hasMaskStrokes.value = false
      isDrawing.value = false
      isProcessing.value = false
      error.value = null
      coverDifficulty.value = 0
      clearDebounce()
      cancelPendingRequest()
    })

    onUnmounted(() => {
      clearDebounce()
      cancelPendingRequest()
    })

    return {
      baseImage,
      maskCanvas,
      canvasContainer,
      isDrawing,
      brushSize,
      hasMaskStrokes,
      isProcessing,
      isSaving,
      error,
      coverDifficulty,
      displayImageUrl,
      currentImageUrl,
      hasResult,
      onImageLoad,
      toggleDrawing,
      clearMask,
      startStroke,
      continueStroke,
      endStroke,
      resetToOriginal,
      handleSave,
    }
  },
}
</script>

<style scoped>
.cover-processing {
  border: 3px solid #7367f0;
  animation: pulse-brightness 1.5s ease-in-out infinite;
}

@keyframes pulse-brightness {
  0%, 100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.25);
  }
}
</style>
